package com.arena_web.ui.components.chat

import androidx.compose.runtime.*
import com.arena_web.data.dto.ChatListItemDto
import com.arena_web.data.dto.MessageDto
import com.arena_web.ui.components.cards.PlayerCard
import com.arena_web.ui.components.cards.PlayerCardSize
import com.arena_web.ui.components.widgets.Avatar
import com.arena_web.ui.components.widgets.AvatarSize
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.AppIcons
import com.arena_web.ui.theme.AppColors
import com.arena_web.ui.theme.LocalSportColors
import com.arena_web.ui.theme.bodyMLight
import com.arena_web.ui.theme.bodyMRegular
import com.arena_web.ui.theme.bodyXlMedium
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.TextOverflow
import com.varabyte.kobweb.compose.css.WhiteSpace
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.*

@Composable
fun ChatListItem(
    chat: ChatListItemDto,
    isSelected: Boolean,
    isOnline: Boolean = false,
    onClick: () -> Unit
) {
    val t = LocalAppStrings.current
    val sportColors = LocalSportColors.current
    var isHovered by remember { mutableStateOf(false) }
    val hasUnread = chat.unreadCount > 0

    Row(
        modifier = Modifier
            .fillMaxWidth()
            .cursor(Cursor.Pointer)
            .onClick { onClick() }
            .attrsModifier {
                onMouseEnter { isHovered = true }
                onMouseLeave { isHovered = false }
            }
            .styleModifier {
                property("gap", s(8))
                property("transition", "background-color 0.15s ease")

                property("border", "3px solid transparent")
                property("border-radius", s(14))

                if (isSelected) {
                    property("padding", "${s(12)} ${s(16)} ${s(12)} ${s(14)}")
                    property("background",
                        "linear-gradient(var(--l-hover2, #091a40), var(--l-hover2, #091a40)) padding-box, " +
                        "linear-gradient(90deg, #0061FF, #3C79F100 5%) border-box"
                    )
                } else {
                    property("padding", "${s(12)} ${s(16)} ${s(12)} ${s(12)}")
                    property("background",
                        if (isHovered) "rgba(255,255,255,0.03)" else "transparent"
                    )
                }
            },
        verticalAlignment = Alignment.CenterVertically
    ) {
        // Avatar with online indicator. For DIRECT chats with a generated
        // player card we render the same Highlight-sized PlayerCard used in
        // the followers list; otherwise fall back to the round Google-style
        // Avatar (groups never have a player card).
        val isDirect = chat.type.uppercase() == "DIRECT"
        Box(modifier = Modifier.styleModifier { property("position", "relative") }) {
            if (isDirect && chat.cardImageUrl != null) {
                Box(
                    modifier = Modifier
                        .styleModifier {
                            property("flex-shrink", "0")
                            property("width", s(40))
                            property("height", s(60))
                            property("display", "flex")
                            property("align-items", "center")
                            property("justify-content", "center")
                        },
                    contentAlignment = Alignment.Center
                ) {
                    PlayerCard(
                        playerName = "",
                        rating = 0,
                        position = "",
                        sport = "football",
                        size = PlayerCardSize.Highlight,
                        cardFrameUrl = chat.cardImageUrl,
                        avatarUrl = chat.cardAvatarUrl ?: AppIcons.EmptyAvatar,
                        hideRating = true,
                        hideShadow = true,
                    )
                }
            } else {
                Avatar(
                    imageUrl = chat.imageUrl,
                    name = chat.name ?: t.chatDefaultName,
                    size = AvatarSize.SMALL
                )
            }
            if (isOnline) {
                Box(
                    modifier = Modifier
                        .sSize(8)
                        .borderRadius(50.percent)
                        .backgroundColor(AppColors.PrimaryMint)
                        .styleModifier {
                            property("position", "absolute")
                            property("bottom", s(0))
                            property("right", s(0))
                            property("border", "0.5px solid var(--l-s3, #02040b)")
                        }
                )
            }
        }

        // Name + last message
        Column(
            modifier = Modifier
                .weight(1f)
                .overflow(Overflow.Hidden)
                .styleModifier {
                    property("min-width", "0")
                    property("gap", s(4))
                }
        ) {
            SpanText(
                chat.name ?: t.chatDefaultName,
                modifier = Modifier
                    .bodyXlMedium()
                    .color(com.arena_web.ui.theme.ThemeVars.Text2)
                    .whiteSpace(WhiteSpace.NoWrap)
                    .overflow(Overflow.Hidden)
                    .textOverflow(TextOverflow.Ellipsis)
            )
            if (chat.lastMessage != null) {
                SpanText(
                    getMessagePreview(chat.lastMessage),
                    modifier = Modifier
                        .bodyMLight()
                        .color(Color("var(--arena-text-muted)"))
                        .whiteSpace(WhiteSpace.NoWrap)
                        .overflow(Overflow.Hidden)
                        .textOverflow(TextOverflow.Ellipsis)
                )
            }
        }

        // Time + unread badge
        Column(
            modifier = Modifier.styleModifier {
                property("align-items", "flex-end")
                property("gap", s(8))
                property("flex-shrink", "0")
            },
            horizontalAlignment = Alignment.End
        ) {
            SpanText(
                if (chat.lastMessage != null) ChatTimeUtils.formatChatTime(chat.lastMessage.createdAt) else "",
                modifier = Modifier
                    .bodyMLight()
                    .color(Color("var(--arena-text-muted)"))
            )
            if (hasUnread) {
                Box(
                    modifier = Modifier
                        .sSize(20)
                        .borderRadius(50.percent)
                        .backgroundColor(sportColors.primary),
                    contentAlignment = Alignment.Center
                ) {
                    SpanText(
                        if (chat.unreadCount > 99) "99+" else chat.unreadCount.toString(),
                        modifier = Modifier
                            .bodyMRegular()
                            .color(com.arena_web.ui.theme.ThemeVars.Text2)
                            .styleModifier { property("transform", "translateY(1px)") }
                    )
                }
            }
        }
    }
}

private fun getMessagePreview(message: MessageDto): String {
    val text = when (message.type.uppercase()) {
        "IMAGE" -> "Photo"
        "AUDIO", "VOICE" -> "Audio"
        "VIDEO" -> "Video"
        "FILE", "DOCUMENT" -> message.fileName ?: "File"
        "SYSTEM" -> message.content
        else -> message.content
    }
    return if (text.length > 30) text.take(30) + "..." else text
}
